import React from 'react'
import { cn } from '../../lib/utils'
import { UIBox } from '../layout/ui-box'

export interface UITableStickyProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * 테이블 컨테이너의 최대 높이 (기본값: 400px)
   */
  maxHeight?: string
  /**
   * Sticky header의 top 위치 (기본값: 0)
   */
  stickyTop?: string
  /**
   * 테이블의 최소 너비 (기본값: 1200px)
   */
  minTableWidth?: string
  /**
   * table 요소에 적용할 추가 className
   */
  tableClassName?: string
  /**
   * 스크롤 가능한 영역임을 나타내는 접근성 라벨
   */
  'aria-label'?: string
  /**
   * 스크롤 가능한 영역에 대한 설명
   */
  'aria-describedby'?: string
}

export interface UITableStickyHeaderProps extends React.HTMLAttributes<HTMLTableSectionElement> {
  /**
   * Sticky header의 top 위치 (기본값: 0)
   */
  stickyTop?: string
}

export interface UITableStickyHeadProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  /**
   * 정렬 방향
   */
  align?: 'left' | 'center' | 'right'
}

export interface UITableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {}
export interface UITableCellProps extends React.TdHTMLAttributes<HTMLTableCellElement> {
  /**
   * 정렬 방향
   */
  align?: 'left' | 'center' | 'right'
}
export interface UITableBodyProps extends React.HTMLAttributes<HTMLTableSectionElement> {}

/**
 * Sticky Header 테이블 컴포넌트
 * 고정 헤더 기능을 제공하는 테이블 컴포넌트입니다.
 * 직접적인 table 구조로 sticky positioning이 확실히 작동합니다.
 */
export const UITableSticky = React.forwardRef<
  HTMLDivElement,
  UITableStickyProps
>(({
  className,
  maxHeight = "400px",
  stickyTop = "0",
  minTableWidth = "1200px",
  tableClassName,
  children,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  ...props
}, ref) => {
  const hasMaxHeight = maxHeight !== "none"
  return (
    <UIBox
      ref={ref}
      className={cn(
        "relative w-full max-w-full border border-border rounded-lg shadow-sm bg-background overflow-x-auto",
        hasMaxHeight ? "overflow-y-auto" : "overflow-y-hidden",
        className
      )}
      style={{ maxHeight: hasMaxHeight ? maxHeight : undefined }}
      role="region"
      aria-label={ariaLabel || "스크롤 가능한 테이블"}
      aria-describedby={ariaDescribedBy}
      tabIndex={0}
      {...props}
    >
      <table className={cn("w-full table-fixed caption-bottom text-sm", tableClassName)} style={{ minWidth: minTableWidth }}>
        {children}
      </table>
    </UIBox>
  )
})

/**
 * Sticky Header 컴포넌트
 * thead 요소에 직접 sticky를 적용하여 확실한 고정 효과를 제공합니다.
 */
export const UITableStickyHeader = React.forwardRef<
  HTMLTableSectionElement,
  UITableStickyHeaderProps
>(({ className, stickyTop = "0", ...props }, ref) => (
  <thead
    ref={ref}
    className={cn(
      "sticky bg-background border-b border-border z-10",
      className
    )}
    style={{ top: stickyTop }}
    aria-label="고정된 테이블 헤더"
    {...props}
  />
))

/**
 * Sticky Header Cell 컴포넌트
 * sticky header 내에서 사용되는 헤더 셀입니다.
 */
export const UITableStickyHead = React.forwardRef<
  HTMLTableCellElement,
  UITableStickyHeadProps
>(({ className, align = "left", ...props }, ref) => (
  <th
    ref={ref}
    scope="col"
    className={cn(
      "h-10 px-3 text-left font-medium text-foreground/70 border-b border-border bg-background",
      align === 'center' && "text-center",
      align === 'right' && "text-right",
      className
    )}
    {...props}
  />
))

/**
 * Sticky Table Body 컴포넌트
 * UITableSticky 내부에서 사용하는 tbody 래퍼입니다.
 */
export const UITableStickyBody = React.forwardRef<
  HTMLTableSectionElement,
  UITableBodyProps
>(({ className, ...props }, ref) => (
  <tbody ref={ref} role="rowgroup" className={className} {...props} />
))

/**
 * Sticky Table Row 컴포넌트
 * UITableSticky 내부에서 사용하는 tr 래퍼입니다.
 */
export const UITableStickyRow = React.forwardRef<
  HTMLTableRowElement,
  UITableRowProps
>(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    role="row"
    className={cn(
      "border-b border-border/50 hover:bg-muted/30 transition-colors duration-200",
      className
    )}
    {...props}
  />
))

/**
 * Sticky Table Cell 컴포넌트
 * UITableSticky 내부에서 사용하는 td 래퍼입니다.
 */
export const UITableStickyCell = React.forwardRef<
  HTMLTableCellElement,
  UITableCellProps
>(({ className, align = "left", style, ...props }, ref) => {
  return (
    <td
      ref={ref}
      role="cell"
      className={cn(
        "px-3 py-2 text-foreground overflow-hidden",
        className
      )}
      style={{
        textAlign: align,
        ...style
      }}
      {...props}
    />
  )
})

// Convenience re-exports from ui-table for components not specific to sticky
export { UITableCaption, UITableFooter } from './ui-table'

UITableSticky.displayName = "UITableSticky"
UITableStickyHeader.displayName = "UITableStickyHeader"
UITableStickyHead.displayName = "UITableStickyHead"
UITableStickyBody.displayName = "UITableStickyBody"
UITableStickyRow.displayName = "UITableStickyRow"
UITableStickyCell.displayName = "UITableStickyCell"